import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { getFirestore, collection, query, getDocs, deleteDoc, doc, where, setDoc, addDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useComparison } from '../contexts/ComparisonContext';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import '../flaticon.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import CustomDropdown from './CustomDropdown';
import CustomMultiSelect from './CustomMultiSelect';
import SEO from './SEO';
import BreadcrumbNav from './seo/BreadcrumbNav';
import { useCompanySettings } from '../hooks/useCompanySettings';
import {
  buildListingBreadcrumbs,
  buildListingSeoMeta,
  buildCollectionPageSchema,
  buildListingCanonicalPath,
  buildListingRoutePath,
  parseListingFiltersFromUrl,
  getPaginationRelLinks,
  LISTING_PAGE_SIZE,
  slugify,
  buildPropertyPath,
  triggerSeoRegeneration,
  resolveOfficialBaseUrl,
  generatePropertyImageAlt,
} from '../utils/seo';
import { getPropertyTypeLabel, isPropertyTypeSlug } from '../constants/propertyTypes';
import { useMapSettings } from '../hooks/useMapSettings';
import ScrollToTop from './ScrollToTop';
import PreLoader from './PreLoader';
import PropertyGridSkeleton from './PropertyGridSkeleton';
import { showNotification } from './Notification';
import MapErrorBoundary from './MapErrorBoundary';
import { logPropertyUpdate, logPropertyDelete, buildPropertyChangeDiff } from '../utils/logger';
import { getPropertyDisplayAddress } from '../utils/propertyAddress';
import {
  fetchPropertiesCatalog,
  fetchCondominiumsCatalog,
  applyPublicPropertyFilters,
  sortPropertyList,
  invalidateAllPropertiesCatalogs,
} from '../services/propertiesCatalog';
import { CATALOG_NS, getCachedCatalog } from '../services/catalogCache';

const Select = lazy(() => import('react-select'));
const PropertyMap = lazy(() => import('./PropertyMap'));
const MapboxPropertyMap = lazy(() => import('./MapboxPropertyMap'));
const QuickEditModal = lazy(() => import('./QuickEditModal'));

const LIST_INITIAL_PAGE_SIZE = 8;

const SelectFallback = () => (
  <div className="h-[46px] w-full rounded-lg border border-[#cfd4dd] bg-gray-50 animate-pulse" aria-hidden="true" />
);

const MapViewFallback = () => (
  <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
    <span className="text-gray-500">Carregando mapa...</span>
  </div>
);

const initialFilters = {
  search: '',
  type: '',
  location: '',
  neighborhood: '',
  purpose: '',
  minPrice: '',
  maxPrice: '',
  bedrooms: '',
  suites: '',
  bathrooms: '',
  parkingSpaces: '',
  minArea: '',
  maxArea: '',
  isGatedCommunity: '',
  orderBy: '',
  condominiumLink: '',
  condominiumId: '',
  condominiumName: '',
  propertyStatus: ''
};

function PropertyList() {
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [properties, setProperties] = useState([]);
  const [allFilteredProperties, setAllFilteredProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [loading, setLoading] = useState(() => {
    try {
      return !getCachedCatalog(CATALOG_NS.PROPERTIES, 'public:all');
    } catch {
      return true;
    }
  });
  const [isPreLoading, setIsPreLoading] = useState(() => {
    try {
      return !getCachedCatalog(CATALOG_NS.PROPERTIES, 'public:all');
    } catch {
      return true;
    }
  });
  const [, setLastDoc] = useState(null);
  const [hasMoreProperties, setHasMoreProperties] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({ ...initialFilters });
  const [condominiums, setCondominiums] = useState([]);
  const [condominiumsLoading, setCondominiumsLoading] = useState(false);
  const [condominiumPropertyCounts, setCondominiumPropertyCounts] = useState({});
  const { user } = useAuth();
  const { canEditProperties, canDeleteProperties, canCreateProperties, canAccessAdmin } = usePermissions();
  const { addToComparison, removeFromComparison, isInComparison } = useComparison();
  const navigate = useNavigate();
  const [viewType, setViewType] = useState('grid');
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [hoveredPropertyId, setHoveredPropertyId] = useState(null);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const [mapKey] = useState(0); // Chave para forçar recriação do mapa
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    property: null
  });
  const [availableCities, setAvailableCities] = useState([]);
  const [availableNeighborhoods, setAvailableNeighborhoods] = useState([]);
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  // Estrutura nova: /propriedades/{cidade}[/{bairro|tipo}][/{tipo}]
  // Compat: rota antiga /propriedades/tipo/:typeSlug (tipo global)
  const {
    citySlug,
    neighborhoodSlug,
    typeSlug,
  } = useMemo(() => {
    if (routeParams.typeSlug) {
      return { citySlug: null, neighborhoodSlug: null, typeSlug: routeParams.typeSlug };
    }
    const seg1 = routeParams.seg1 || routeParams.slugAndId;
    const { seg2, seg3 } = routeParams;
    if (!seg1) return { citySlug: null, neighborhoodSlug: null, typeSlug: null };

    // 3 segmentos: cidade / bairro / tipo
    if (seg3) {
      return { citySlug: seg1, neighborhoodSlug: seg2, typeSlug: seg3 };
    }
    // 2 segmentos: cidade / (tipo ou bairro)
    if (seg2) {
      if (isPropertyTypeSlug(seg2)) {
        return { citySlug: seg1, neighborhoodSlug: null, typeSlug: seg2 };
      }
      return { citySlug: seg1, neighborhoodSlug: seg2, typeSlug: null };
    }
    // 1 segmento: cidade
    return { citySlug: seg1, neighborhoodSlug: null, typeSlug: null };
  }, [routeParams]);
  const [quickEditModal, setQuickEditModal] = useState({
    isOpen: false,
    property: null
  });
  const [shareModal, setShareModal] = useState({
    isOpen: false,
    property: null
  });
  const [userFavorites, setUserFavorites] = useState([]);
  const { companyName, seoCanonicalUrl } = useCompanySettings();
  const { mapSettings } = useMapSettings();


  // Estados para animações
  const [, setIsLoaded] = useState(false);
  const [visibleElements, setVisibleElements] = useState({
    header: false,
    filters: false,
    properties: false
  });
  
  // Estado para controlar visibilidade de cada card
  const [visibleCards, setVisibleCards] = useState({});

  // Refs para os elementos que serão observados
  const headerRef = useRef(null);
  const filtersRef = useRef(null);
  const propertiesRef = useRef(null);
  
  // Refs para cada card de propriedade
  const propertyRefs = useRef({});
  
  // Ref para o elemento sentinela do scroll infinito
  const loadMoreRef = useRef(null);
  /** Catálogo completo em memória (1 fetch Firestore por TTL). */
  const catalogRef = useRef(null);

  const orderByOptions = [
    { value: 'price_asc', label: 'Menor preço' },
    { value: 'price_desc', label: 'Maior preço' },
    { value: 'newest', label: 'Mais recentes' },
    { value: 'oldest', label: 'Mais antigos' },
    { value: 'area_asc', label: 'Menor área' },
    { value: 'area_desc', label: 'Maior área' }
  ];

  // Estilos customizados para o React Select
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? 'var(--primary-color, #000000)' : '#cfd4dd',
      borderRadius: '0.5rem',
      minHeight: '46px',
      height: '46px',
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'var(--primary-color, #000000)'
      },
      '&:focus, &:focus-within': {
        border: '2px solid var(--primary-color, #000000)',
        boxShadow: 'none'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? 'var(--primary-color, #3B82F6)'
        : state.isFocused
        ? '#f3f4f6'
        : 'white',
      color: state.isSelected
        ? 'var(--secondary-color, #ffffff)'
        : '#111827',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: state.isSelected
          ? 'var(--primary-color, #3B82F6)'
          : '#e5e7eb',
        color: state.isSelected
          ? 'var(--secondary-color, #ffffff)'
          : '#111827'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    singleValue: (base, { selectProps }) => ({
      ...base,
      color: selectProps.hasValue ? '#111827' : '#6B7280'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      outline: 'none',
      border: 'none',
      boxShadow: 'none',
      '& input': {
        outline: 'none !important',
        boxShadow: 'none !important',
        borderColor: 'transparent !important'
      },
      '& input:focus': {
        outline: 'none !important',
        boxShadow: 'none !important',
        borderColor: 'transparent !important'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e2e8f0',
      borderRadius: '0.375rem'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#1f2937'
    }),
    multiValueRemove: (base) => ({
      ...base,
      ':hover': {
        backgroundColor: '#cbd5e1',
        color: '#1f2937'
      }
    }),
    menu: (base) => ({
      ...base,
      cursor: 'pointer',
      zIndex: 9999
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    })
  };

  // Converter os dados para o formato do React Select
  const typeOptions = [
    { value: '', label: 'Tipo Imóvel' },
    { value: 'house', label: 'Casa' },
    { value: 'apartment', label: 'Apartamento' },
    { value: 'loft', label: 'Loft' },
    { value: 'warehouse', label: 'Galpão' },
    { value: 'store', label: 'Loja' },
    { value: 'building', label: 'Prédio' },
    { value: 'office', label: 'Sala' },
    { value: 'land', label: 'Terreno' },
    { value: 'farm', label: 'Sítio' },
    { value: 'countryHouse', label: 'Chácara' }
  ];

  const purposeOptions = [
    { value: '', label: 'Finalidade' },
    { value: 'sale', label: 'Venda' },
    { value: 'rent', label: 'Aluguel' }
  ];

  const cityFilterOptions = useMemo(() =>
    availableCities.map(city => ({
      value: city,
      label: city,
      searchText: city
    })),
    [availableCities]
  );

  const neighborhoodFilterOptions = useMemo(() =>
    availableNeighborhoods.map(n => ({
      value: n,
      label: n,
      searchText: n
    })),
    [availableNeighborhoods]
  );

  const priceRangeOptions = [
    { value: '-', label: 'Qualquer valor' },
    { value: '0-200000', label: 'Até R$ 200.000' },
    { value: '200000-400000', label: 'De R$ 200.000 até R$ 400.000' },
    { value: '400000-600000', label: 'De R$ 400.000 até R$ 600.000' },
    { value: '600000-800000', label: 'De R$ 600.000 até R$ 800.000' },
    { value: '800000-1000000', label: 'De R$ 800.000 até R$ 1.000.000' },
    { value: '1000000-2000000', label: 'De R$ 1.000.000 até R$ 2.000.000' },
    { value: '2000000-', label: 'Acima de R$ 2.000.000' }
  ];

  const areaRangeOptions = [
    { value: '-', label: 'Qualquer área' },
    { value: '0-50', label: 'Até 50m²' },
    { value: '50-75', label: '50m² até 75m²' },
    { value: '75-150', label: '75m² até 150m²' },
    { value: '150-200', label: '150m² até 200m²' },
    { value: '200-300', label: '200m² até 300m²' },
    { value: '300-', label: 'A partir de 300m²' }
  ];

  const countOptions = [
    { value: '', label: 'Qualquer' },
    { value: '1', label: '1+' },
    { value: '2', label: '2+' },
    { value: '3', label: '3+' },
    { value: '4', label: '4+' }
  ];

  const condominiumLinkOptions = [
    { value: '', label: 'Qualquer' },
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' }
  ];

  // Opções de status do imóvel
  const propertyStatusOptions = [
    { value: '', label: 'Qualquer' },
    { value: 'reserved', label: 'Reservado' },
    { value: 'featured', label: 'Em destaque' },
    { value: 'launch', label: 'Lançamento' },
    { value: 'preLaunch', label: 'Pré lançamento' }
  ];

  const filtersStateRef = useRef(filters);
  useEffect(() => {
    filtersStateRef.current = filters;
  }, [filters]);

  const applyCatalogToUi = useCallback((catalog, activeFilters, isInitial = true) => {
    const { filtered, cities, neighborhoods, counts } = applyPublicPropertyFilters(
      catalog,
      activeFilters
    );

    setCondominiumPropertyCounts(counts);
    setAvailableCities(cities);
    setAvailableNeighborhoods(neighborhoods);
    setTotalProperties(filtered.length);
    setAllFilteredProperties(filtered);

    if (isInitial) {
      const initialProperties = viewType === 'map' ? filtered : filtered.slice(0, LIST_INITIAL_PAGE_SIZE);
      setProperties(initialProperties);
      setHasMoreProperties(filtered.length > initialProperties.length);
    } else {
      const currentLength = properties.length;
      const newProperties = filtered.slice(currentLength, currentLength + LIST_INITIAL_PAGE_SIZE);
      setProperties((prev) => [...prev, ...newProperties]);
      setHasMoreProperties(filtered.length > currentLength + LIST_INITIAL_PAGE_SIZE);
    }

    setLastDoc(
      filtered.length >
        (isInitial
          ? viewType === 'map'
            ? filtered.length
            : LIST_INITIAL_PAGE_SIZE
          : properties.length + LIST_INITIAL_PAGE_SIZE)
    );
  }, [properties.length, viewType]);

  const loadProperties = useCallback(async (isInitial = false, filtersOverride = null, { force = false } = {}) => {
    const activeFilters = filtersOverride || filtersStateRef.current || filters;
    const cached = !force ? getCachedCatalog(CATALOG_NS.PROPERTIES, 'public:all') : null;
    const canUseWarmCache = Boolean(cached && !cached.stale);

    if (!canUseWarmCache) setLoading(true);
    try {
      const catalog = await fetchPropertiesCatalog({ scope: 'public', force });
      catalogRef.current = catalog;
      applyCatalogToUi(catalog, activeFilters, isInitial);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    } finally {
      setLoading(false);
    }
  }, [applyCatalogToUi, filters]);

  const routeSlugs = useMemo(
    () => ({ citySlug, neighborhoodSlug, typeSlug }),
    [citySlug, neighborhoodSlug, typeSlug]
  );

  const navigateToListingFilters = useCallback((patch = {}, { replace = true } = {}) => {
    const nextFilters = { ...filtersStateRef.current, ...patch };
    filtersStateRef.current = nextFilters;
    navigate(buildListingRoutePath(nextFilters), { replace });
  }, [navigate]);

  // URL (path + query) é a fonte da verdade dos filtros
  useEffect(() => {
    const parsed = parseListingFiltersFromUrl(routeSlugs, searchParams, {
      cities: availableCities,
      neighborhoods: availableNeighborhoods,
    });
    const urlFilters = { ...initialFilters, ...parsed };
    setFilters(urlFilters);
    loadProperties(true, urlFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL is source of truth; avoid reload loops
  }, [routeSlugs, searchParams]);

  // Normaliza slug → nome legível nos selects quando as listas carregam
  useEffect(() => {
    if (!citySlug || !availableCities.length) return;
    const city = availableCities.find((c) => slugify(c) === citySlug);
    if (!city) return;
    setFilters((prev) => (prev.location === city ? prev : { ...prev, location: city }));
  }, [citySlug, availableCities]);

  useEffect(() => {
    if (!neighborhoodSlug || !availableNeighborhoods.length) return;
    const nb = availableNeighborhoods.find((n) => slugify(n) === neighborhoodSlug);
    if (!nb) return;
    setFilters((prev) => (prev.neighborhood === nb ? prev : { ...prev, neighborhood: nb }));
  }, [neighborhoodSlug, availableNeighborhoods]);

  useEffect(() => {
    let idleId;
    let timeoutId;
    const loadCondominiums = async () => {
      setCondominiumsLoading(true);
      try {
        const list = await fetchCondominiumsCatalog();
        const sorted = [...list].sort((a, b) =>
          String(a.title || '').localeCompare(String(b.title || ''), 'pt-BR', {
            sensitivity: 'base'
          })
        );
        setCondominiums(sorted);
      } catch (error) {
        console.error('Erro ao carregar condomínios:', error);
      } finally {
        setCondominiumsLoading(false);
      }
    };

    const run = () => {
      loadCondominiums();
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(run, 400);
    }
    return () => {
      if (idleId != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  // Scroll ao topo ao mudar filtros/rota da listagem
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [routeSlugs, searchParams]);

  const totalLinkedToCondominium = useMemo(
    () => Object.values(condominiumPropertyCounts).reduce((sum, n) => sum + n, 0),
    [condominiumPropertyCounts]
  );

  const condominiumSelectOptions = useMemo(
    () =>
      condominiums.map((condo) => {
        const count = condominiumPropertyCounts[condo.id] || 0;
        const name = condo.code
          ? `${condo.title || 'Sem título'} (Cód. ${condo.code})`
          : condo.title || condo.id;
        return {
          value: condo.id,
          label: `${name} — ${count} imóve${count === 1 ? 'l' : 'is'}`
        };
      }),
    [condominiums, condominiumPropertyCounts]
  );

  // Carrega os favoritos do usuário quando o usuário mudar e mantém listener em tempo real
  useEffect(() => {
    if (!user) {
      // Carrega favoritos do localStorage para usuários não logados
      const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
      setUserFavorites(localFavorites);
      
      // Listener para mudanças no localStorage (sincronização entre abas)
      const handleStorageChange = (e) => {
        if (e.key === 'localFavorites') {
          const updatedFavorites = JSON.parse(e.newValue || '[]');
          setUserFavorites(updatedFavorites);
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }

    const db = getFirestore();
    const favoritesQuery = query(
      collection(db, 'favorites'),
      where('userId', '==', user.uid)
    );
    
    // Listener em tempo real para mudanças nos favoritos
    const unsubscribe = onSnapshot(favoritesQuery, (snapshot) => {
      const favoriteIds = snapshot.docs.map(doc => doc.data().propertyId);
      setUserFavorites(favoriteIds);
    }, (error) => {
      // Erro ao escutar mudanças nos favoritos
    });

    // Cleanup do listener quando o componente desmonta ou usuário muda
    return () => unsubscribe();
  }, [user]);

  // Função para finalizar o pre-loading
  const handlePreLoadingComplete = () => {
    setIsPreLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      setIsPreLoading(false);
    }
  }, [loading]);

  // Controlar animações de entrada baseadas no scroll - Otimizado
  useEffect(() => {
    // Só inicia as animações após o pre-loading terminar
    if (!isPreLoading) {
      setIsLoaded(true);
      
      // Configuração do Intersection Observer
      const observerOptions = {
        threshold: 0.05, // Reduzido para melhor performance
        rootMargin: '50px' // Simplificado
      };

      const observer = new IntersectionObserver((entries) => {
        // Batch de atualizações para reduzir re-renders
        const updates = { elements: {}, cards: {} };
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-animate');
            if (elementId) {
              if (elementId === 'property-card') {
                const cardId = entry.target.getAttribute('data-card-id');
                updates.cards[cardId] = true;
              } else {
                updates.elements[elementId] = true;
              }
              observer.unobserve(entry.target);
            }
          }
        });

        // Atualizar tudo de uma vez
        if (Object.keys(updates.elements).length > 0) {
          setVisibleElements(prev => ({ ...prev, ...updates.elements }));
        }
        if (Object.keys(updates.cards).length > 0) {
          setVisibleCards(prev => ({ ...prev, ...updates.cards }));
        }
      }, observerOptions);

      // Função para configurar o observer
      const setupObserver = () => {
        // Observar elementos principais
        const elementsToObserve = [
          headerRef.current,
          filtersRef.current,
          propertiesRef.current
        ].filter(Boolean);

        elementsToObserve.forEach(element => {
          if (element) {
            observer.observe(element);
          }
        });

        // Observar apenas os primeiros 8 cards para melhor performance inicial
        const propertyElements = Object.values(propertyRefs.current).slice(0, 8);
        propertyElements.forEach(element => {
          if (element) {
            observer.observe(element);
          }
        });
      };

      // Configurar o observer após um delay menor
      const timer = setTimeout(setupObserver, 500);

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
  }, [isPreLoading]); // Removida dependência de properties para evitar re-renders

  const handleClearFilters = async () => {
    try {
      setLoading(true);

      setFilters(initialFilters);
      filtersStateRef.current = { ...initialFilters };
      setAvailableNeighborhoods([]);
      setSelectedPropertyId(null);
      setHoveredPropertyId(null);
      setVisibleProperties([]);

      navigate('/propriedades', { replace: true });

      let catalog = catalogRef.current;
      if (!catalog) {
        catalog = await fetchPropertiesCatalog({ scope: 'public' });
        catalogRef.current = catalog;
      }
      applyCatalogToUi(catalog, initialFilters, true);

      if (viewType === 'map') {
        window.location.reload();
      }
    } catch (error) {
      // Erro ao limpar filtros
    } finally {
      setLoading(false);
    }
  };

  // Novo useEffect para observar mudanças nos filtros — removido:
  // filtros agora sincronizam via URL (path + query) em um único efeito acima.

  const handleFilterChange = (name, value) => {
    const patch = { [name]: value };

    if (name === 'location') {
      patch.neighborhood = '';
      if (!value) patch.location = '';
    }

    if (name === 'condominiumLink' && value !== 'true') {
      patch.condominiumId = '';
      patch.condominiumName = '';
    }

    if (name === 'condominiumId') {
      const selected = condominiumSelectOptions.find((opt) => opt.value === value);
      patch.condominiumName = selected?.label || '';
    }

    if (name === 'search') {
      if (searchTimeout) clearTimeout(searchTimeout);
      setFilters((prev) => ({ ...prev, search: value }));
      const timeoutId = setTimeout(() => {
        navigateToListingFilters({ search: value });
      }, 500);
      setSearchTimeout(timeoutId);
      return;
    }

    if (name === 'orderBy') {
      const sorted = sortPropertyList(allFilteredProperties, value || 'newest');
      const pageSize = viewType === 'map' ? sorted.length : LIST_INITIAL_PAGE_SIZE;
      setAllFilteredProperties(sorted);
      setProperties(sorted.slice(0, pageSize));
      setHasMoreProperties(sorted.length > pageSize);
      navigateToListingFilters(patch);
      return;
    }

    navigateToListingFilters(patch);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigateToListingFilters({});
  };

  const openDeleteModal = (property) => {
    setDeleteModal({
      isOpen: true,
      property
    });
  };

  const handleDelete = async () => {
    try {
      const db = getFirestore();
      if (user) {
        await logPropertyDelete(user, deleteModal.property);
      }
      await deleteDoc(doc(db, 'properties', deleteModal.property.id));
      invalidateAllPropertiesCatalogs();
      catalogRef.current = (catalogRef.current || []).filter(
        (p) => p.id !== deleteModal.property.id
      );
      triggerSeoRegeneration().catch(() => {});
      setProperties(properties.filter(p => p.id !== deleteModal.property.id));
      setAllFilteredProperties((prev) => prev.filter((p) => p.id !== deleteModal.property.id));
      setDeleteModal({ isOpen: false, property: null });
    } catch (error) {
      // Erro ao remover imóvel
      alert('Erro ao remover imóvel');
    }
  };

  const handleDuplicate = async (property) => {
    // Navega para a página de duplicação com o ID da propriedade
    navigate(`/duplicar/${property.id}`);
  };

  // Função auxiliar para verificar se o usuário pode ver ações do imóvel
  const canViewPropertyActions = (property) => {
    // Se for admin legado, pode ver todas as ações em todos os imóveis
    if (user?.isAdmin) {
      return {
        canEdit: true,
        canDelete: true,
        canDuplicate: true
      };
    }
    
    // Se tem accessAdmin mas não é admin legado, é corretor com acesso ao admin
    // Nesse caso, só pode ver ações nos imóveis vinculados a ele
    if (user && canAccessAdmin()) {
      const propertyBelongsToUser = 
        property.brokerId === user.uid ||
        property.createdBy === user.uid ||
        property.assignedTo === user.uid;
      
      if (propertyBelongsToUser) {
        // Verifica permissões do perfil
        return {
          canEdit: canEditProperties(),
          canDelete: canDeleteProperties(),
          canDuplicate: canCreateProperties() // Duplicar requer permissão de criar
        };
      }
    }
    
    // Se não pertence ao corretor ou não tem acesso, não pode ver ações
    return {
      canEdit: false,
      canDelete: false,
      canDuplicate: false
    };
  };


  useEffect(() => {
    // Verifica se deve mostrar os filtros avançados
    const showFilters = searchParams.get('showFilters') === 'true';
    if (showFilters) {
      setShowAdvancedFilters(true);
    }
  }, [searchParams]);

  useEffect(() => {
    // Força o modo grid em telas pequenas
    const handleResize = () => {
      if (window.innerWidth < 640 && (viewType === 'list' || viewType === 'map')) {
        setViewType('grid');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Checa na montagem

    return () => window.removeEventListener('resize', handleResize);
  }, [viewType]);

  // Força o modo grid se o mapa estiver desabilitado
  useEffect(() => {
    if (!mapSettings.enabled && viewType === 'map') {
      setViewType('grid');
    }
  }, [mapSettings.enabled, viewType]);

  // Recarregar propriedades quando mudar para modo mapa
  useEffect(() => {
    if (viewType === 'map') {
      loadProperties(true);
      setVisibleProperties(properties); // Inicializar com todas as propriedades
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload map data only when view type changes
  }, [viewType]);

  // Atualizar propriedades visíveis quando as propriedades mudarem
  useEffect(() => {
    if (viewType === 'map') {
      // Se não há propriedades visíveis definidas, usar todas as propriedades
      if (visibleProperties.length === 0 && properties.length > 0) {
        setVisibleProperties(properties);
      }
    }
  }, [properties, viewType, visibleProperties.length]);

  // Scroll infinito - carrega mais propriedades quando chega ao final
  useEffect(() => {
    if (viewType === 'map' || !loadMoreRef.current || loading || !hasMoreProperties) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loading && hasMoreProperties) {
          // Carrega mais 8 propriedades
          const currentLength = properties.length;
          const nextProperties = allFilteredProperties.slice(currentLength, currentLength + 8);
          
          if (nextProperties.length > 0) {
            setProperties(prev => [...prev, ...nextProperties]);
            setHasMoreProperties(allFilteredProperties.length > currentLength + 8);
          } else {
            setHasMoreProperties(false);
          }
        }
      },
      {
        root: null,
        rootMargin: '200px', // Aumentado para começar a carregar mais cedo
        threshold: 0.1
      }
    );

    observer.observe(loadMoreRef.current);
    const loadMoreNode = loadMoreRef.current;

    return () => {
      if (loadMoreNode) {
        observer.unobserve(loadMoreNode);
      }
    };
  }, [viewType, loading, hasMoreProperties, properties.length, allFilteredProperties]);

  // Observar novos cards de propriedades quando mais são carregados
  useEffect(() => {
    if (isPreLoading || viewType === 'map') return;

    const observer = new IntersectionObserver((entries) => {
      const updates = {};
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const cardId = entry.target.getAttribute('data-card-id');
          if (cardId) {
            updates[cardId] = true;
            observer.unobserve(entry.target);
          }
        }
      });

      if (Object.keys(updates).length > 0) {
        setVisibleCards(prev => ({ ...prev, ...updates }));
      }
    }, {
      threshold: 0.05,
      rootMargin: '50px'
    });

    // Observar apenas os novos cards (aqueles que ainda não foram observados)
    Object.entries(propertyRefs.current).forEach(([id, element]) => {
      if (element && !visibleCards[id]) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [properties.length, isPreLoading, viewType, visibleCards]);

  const handleQuickEdit = async (updates) => {
    try {
      const db = getFirestore();
      const propertyRef = doc(db, 'properties', quickEditModal.property.id);
      
      // Mapear os campos antigos para os novos para compatibilidade
      const mappedUpdates = { ...updates };
      
      // Se está alterando o propertyStatus, também atualiza os campos de compatibilidade
      if (updates.propertyStatus) {
        const status = updates.propertyStatus;
        
        // Mapear para campos antigos para compatibilidade
        if (status === 'Disponível') {
          mappedUpdates.isAvailable = true;
          mappedUpdates.isInactive = false;
          mappedUpdates.soldOrRented = false;
        } else {
          // Para Indisponível, Vendido, Alugado ou Cancelado
          mappedUpdates.isAvailable = false;
          mappedUpdates.isInactive = true;
          mappedUpdates.soldOrRented = (status === 'Vendido' || status === 'Alugado');
        }
      }
      
      await setDoc(propertyRef, mappedUpdates, { merge: true });
      
      // Registrar log com diff detalhado
      if (user) {
        const changeDiff = buildPropertyChangeDiff(quickEditModal.property, { ...quickEditModal.property, ...mappedUpdates });
        await logPropertyUpdate(
          user,
          { ...quickEditModal.property, ...mappedUpdates },
          { changeDiff }
        );
      }
      
      // Atualiza a lista local
      setProperties(properties.map(p => 
        p.id === quickEditModal.property.id 
          ? { ...p, ...mappedUpdates }
          : p
      ));
      
      // Fecha o modal se todas as atualizações foram bem sucedidas
      setQuickEditModal({ isOpen: false, property: null });
    } catch (error) {
      // Erro ao atualizar propriedade
      alert('Erro ao atualizar propriedade');
    }
  };

  // Funções de compartilhamento
  const getPropertyUrl = (property) => {
    return `${window.location.origin}${buildPropertyPath(property)}`;
  };

  const getShareText = (property) => {
    return `Confira este imóvel: ${property.title} - ${getPropertyDisplayAddress(property)}`;
  };

  const shareToWhatsApp = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToFacebook = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}&hashtag=${encodeURIComponent('#imoveis #venda #aluguel')}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(property.title)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const shareToEmail = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const subject = `Imóvel: ${property.title}`;
    const body = `${text}\n\nVeja mais detalhes: ${url}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
  };

  const copyLink = async (property) => {
    const url = getPropertyUrl(property);
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copiado para a área de transferência!');
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copiado para a área de transferência!');
    }
  };

  // Funções para gerenciar favoritos

  const toggleFavorite = async (property) => {
    try {
      const isFavorite = userFavorites.includes(property.id);

      if (!user) {
        // Usuário não logado - usa localStorage
        const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
        
        if (isFavorite) {
          // Remove dos favoritos locais
          const updatedFavorites = localFavorites.filter(id => id !== property.id);
          localStorage.setItem('localFavorites', JSON.stringify(updatedFavorites));
          setUserFavorites(updatedFavorites);
          showNotification('Imóvel removido dos favoritos', 'warning', 3000);
        } else {
          // Verifica se já tem 20 favoritos
          if (localFavorites.length >= 20) {
            showNotification('Limite de 20 favoritos atingido. Remova alguns para adicionar novos.', 'error', 5000);
            return;
          }

          // Adiciona aos favoritos locais
          const updatedFavorites = [...localFavorites, property.id];
          localStorage.setItem('localFavorites', JSON.stringify(updatedFavorites));
          setUserFavorites(updatedFavorites);
          showNotification('Imóvel adicionado aos favoritos. Faça login para salvar permanentemente.', 'success', 4000);
        }
      } else {
        // Usuário logado - usa Firestore
        const db = getFirestore();

        if (isFavorite) {
          // Remove dos favoritos
          const favoriteQuery = query(
            collection(db, 'favorites'),
            where('userId', '==', user.uid),
            where('propertyId', '==', property.id)
          );
          
          const favoriteSnapshot = await getDocs(favoriteQuery);
          if (!favoriteSnapshot.empty) {
            await deleteDoc(favoriteSnapshot.docs[0].ref);
            setUserFavorites(prev => prev.filter(id => id !== property.id));
            
            // Mostra notificação de remoção
            showNotification('Imóvel removido dos favoritos', 'warning', 3000);
          }
        } else {
          // Verifica se já tem 20 favoritos
          if (userFavorites.length >= 20) {
            // Mostra notificação de limite atingido
            showNotification('Limite de 20 favoritos atingido. Remova alguns para adicionar novos.', 'error', 5000);
            return;
          }

          // Adiciona aos favoritos
          await addDoc(collection(db, 'favorites'), {
            userId: user.uid,
            propertyId: property.id,
            createdAt: new Date()
          });
          setUserFavorites(prev => [...prev, property.id]);
          
          // Mostra notificação de adição
          showNotification('Imóvel adicionado aos favoritos', 'success', 3000);
        }
      }
    } catch (error) {
      // Erro ao atualizar favoritos
      console.error('Erro ao atualizar favoritos:', error);
      
      // Mostra notificação de erro
      showNotification('Erro ao atualizar favoritos', 'error', 5000);
    }
  };

  const isFavorite = (propertyId) => {
    return userFavorites.includes(propertyId);
  };

  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const baseUrl = resolveOfficialBaseUrl({ seoCanonicalUrl });
  const listingSeo = buildListingSeoMeta(filters, {
    companyName,
    cityName: filters.location,
    neighborhoodName: filters.neighborhood?.split(',')[0],
    typeName: filters.type ? getPropertyTypeLabel(filters.type) : null,
    totalCount: allFilteredProperties.length,
  });
  const breadcrumbItems = buildListingBreadcrumbs(filters, {
    cityName: filters.location,
    neighborhoodName: filters.neighborhood?.split(',')[0],
    typeName: filters.type ? getPropertyTypeLabel(filters.type) : null,
  });
  const totalPages = Math.max(1, Math.ceil(allFilteredProperties.length / LISTING_PAGE_SIZE));
  const paginationLinks = getPaginationRelLinks(filters, currentPage, totalPages, baseUrl);
  // Listagens filtradas (cidade/bairro/tipo/busca) sem resultados não devem ser indexadas
  const isFilteredListing = Boolean(
    citySlug || typeSlug || filters.location || filters.type || filters.search
  );
  const listingRobots =
    isFilteredListing && !loading && allFilteredProperties.length === 0
      ? 'noindex,follow'
      : null;
  const collectionSchema = buildCollectionPageSchema({
    name: listingSeo.title,
    description: listingSeo.description,
    url: buildListingCanonicalPath(filters, currentPage),
    totalItems: allFilteredProperties.length,
    items: allFilteredProperties,
    baseUrl,
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* PreLoader Component */}
      <PreLoader 
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      {/* SEO Component */}
      <SEO
        title={listingSeo.title}
        description={listingSeo.description}
        keywords="imóveis, propriedades, compra, venda, aluguel, casas, apartamentos"
        pageType="website"
        canonicalUrl={`${baseUrl}${buildListingCanonicalPath(filters, currentPage)}`}
        includeSiteSchemas
        supplementarySchemas={[collectionSchema]}
        paginationLinks={paginationLinks}
        robots={listingRobots}
      />
      
      {/* Container com largura limitada para elementos superiores */}
      <div className="max-w-[85rem] mx-auto p-6 w-full">
      {/* Breadcrumbs */}
      <div 
        ref={headerRef}
        data-animate="header"
        className={`mb-8 transition-all duration-1000 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <BreadcrumbNav items={breadcrumbItems} baseUrl={baseUrl} />
      </div>

      <h1 
        className={`text-3xl font-montserrat font-semibold mb-8 text-center transition-all duration-1000 delay-200 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {listingSeo.heading || 'Encontre o imóvel dos seus sonhos'}
      </h1>

        <Suspense fallback={
          <div className="space-y-4">
            <div className="h-[46px] rounded-lg bg-gray-100 animate-pulse" />
            <div className="grid grid-cols-2 gap-3">
              <SelectFallback />
              <SelectFallback />
            </div>
          </div>
        }>
        <form 
          ref={filtersRef}
          data-animate="filters"
          onSubmit={handleSearch} 
          className={`space-y-4 transition-all duration-1000 delay-200 ${visibleElements.filters ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Barra de pesquisa e filtros principais */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-3">
            {/* Campo de busca */}
            <div className="relative lg:w-[35%]">
              <svg 
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={1.5} 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="text"
                placeholder="Nome, código, cidade ou bairro..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full border border-[#cfd4dd] rounded-lg pl-11 pr-4 h-[46px] text-[15px] focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* Filtros e botões */}
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-none lg:flex lg:w-[65%] gap-3 lg:gap-3">
              {/* Tipo e Finalidade na mesma linha em mobile */}
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-none lg:flex gap-3 md:col-span-2 lg:w-[45%]">
                {/* Tipo */}
                <div className="col-span-1 lg:w-1/2">
                  <Select
                    placeholder="Tipo"
                    value={typeOptions.find(option => option.value === filters.type)}
                    onChange={(option) => handleFilterChange('type', option?.value || '')}
                    options={typeOptions}
                    isClearable={filters.type !== ''}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                {/* Finalidade */}
                <div className="col-span-1 lg:w-1/2">
                  <Select
                    placeholder="Finalidade"
                    value={purposeOptions.find(option => option.value === filters.purpose)}
                    onChange={(option) => handleFilterChange('purpose', option?.value || '')}
                    options={purposeOptions}
                    isClearable={filters.purpose !== ''}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>

              {/* Mais Filtros e Limpar na mesma linha */}
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-none lg:flex gap-3 md:col-span-2 lg:w-[35%]">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="w-full lg:w-2/3 px-4 h-[46px] rounded-lg text-[15px] font-medium transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m0 12h9.75m-9.75 0a1.5 1.5 0 0 1 3 0m-3 0a1.5 1.5 0 0 0 3 0m-3.75 0H7.5m9-6h3.75M18 12a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H15" />
                  </svg>
                  {showAdvancedFilters ? 'Ocultar' : 'Avançado'}
                </button>

                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="w-full lg:w-1/2 px-4 h-[46px] rounded-lg text-[15px] font-medium transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Limpar
                </button>
              </div>

              {/* Botão Encontrar */}
              <div className="col-span-1 md:col-span-4 lg:w-[25%]">
                <button
                  type="submit"
                  className="w-full px-4 h-[46px] rounded-lg text-[15px] font-medium transition-colors duration-200 hover:opacity-90"
                  style={{
                    backgroundColor: 'var(--primary-color, #3B82F6)',
                    color: 'var(--secondary-color, #ffffff)'
                  }}
                >
                  Encontrar Imóveis
                </button>
              </div>
            </div>
          </div>

          {/* Filtros avançados */}
          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-[#cfd4dd]">
              {/* Primeira linha - 4 colunas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Cidade</label>
                  <CustomDropdown
                    className="w-full min-w-0"
                    triggerClassName="min-h-[46px] text-[15px]"
                    options={[{ value: '', label: 'Qualquer' }, ...cityFilterOptions]}
                    value={filters.location}
                    onChange={(value) => handleFilterChange('location', value)}
                    placeholder="Qualquer"
                    searchable
                    searchPlaceholder="Digite para buscar..."
                    maxVisibleOptions={10}
                    colorScheme="black"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Bairro</label>
                  <CustomMultiSelect
                    className="w-full min-w-0"
                    triggerClassName="text-[15px]"
                    options={neighborhoodFilterOptions}
                    value={filters.neighborhood.split(',').filter(Boolean)}
                    onChange={(selectedValues) =>
                      handleFilterChange('neighborhood', selectedValues.join(','))
                    }
                    placeholder="Selecione os bairros"
                    disabled={!filters.location}
                    searchable
                    searchPlaceholder="Digite para buscar..."
                    maxVisibleOptions={10}
                    colorScheme="black"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Preço</label>
                  <Select
                    name="priceRange"
                    value={priceRangeOptions.find(option => option.value === `${filters.minPrice}-${filters.maxPrice}`)}
                    onChange={(option) => {
                      const [min, max] = (option?.value || '-').split('-');
                      handleFilterChange('minPrice', min);
                      handleFilterChange('maxPrice', max);
                    }}
                    options={priceRangeOptions}
                    styles={customStyles}
                    placeholder="Qualquer valor"
                    isClearable={filters.minPrice !== '' || filters.maxPrice !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Área (m²)</label>
                  <Select
                    name="areaRange"
                    value={areaRangeOptions.find(option => option.value === `${filters.minArea}-${filters.maxArea}`)}
                    onChange={(option) => {
                      const [min, max] = (option?.value || '-').split('-');
                      handleFilterChange('minArea', min);
                      handleFilterChange('maxArea', max);
                    }}
                    options={areaRangeOptions}
                    styles={customStyles}
                    placeholder="Qualquer área"
                    isClearable={filters.minArea !== '' || filters.maxArea !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>

              {/* Segunda linha - 6 colunas */}
              <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Quartos</label>
                  <Select
                    name="bedrooms"
                    value={countOptions.find(option => option.value === filters.bedrooms)}
                    onChange={(option) => handleFilterChange('bedrooms', option?.value || '')}
                    options={countOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.bedrooms !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Suítes</label>
                  <Select
                    name="suites"
                    value={countOptions.find(option => option.value === filters.suites)}
                    onChange={(option) => handleFilterChange('suites', option?.value || '')}
                    options={countOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.suites !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Banheiros</label>
                  <Select
                    name="bathrooms"
                    value={countOptions.find(option => option.value === filters.bathrooms)}
                    onChange={(option) => handleFilterChange('bathrooms', option?.value || '')}
                    options={countOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.bathrooms !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Vagas</label>
                  <Select
                    name="parkingSpaces"
                    value={countOptions.find(option => option.value === filters.parkingSpaces)}
                    onChange={(option) => handleFilterChange('parkingSpaces', option?.value || '')}
                    options={countOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.parkingSpaces !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Status do Imóvel</label>
                  <Select
                    name="propertyStatus"
                    value={propertyStatusOptions.find(option => option.value === filters.propertyStatus)}
                    onChange={(option) => handleFilterChange('propertyStatus', option?.value || '')}
                    options={propertyStatusOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.propertyStatus !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Condomínio</label>
                  <Select
                    name="condominiumLink"
                    value={condominiumLinkOptions.find(option => option.value === filters.condominiumLink)}
                    onChange={(option) => handleFilterChange('condominiumLink', option?.value || '')}
                    options={condominiumLinkOptions}
                    styles={customStyles}
                    placeholder="Qualquer"
                    isClearable={filters.condominiumLink !== ''}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>

              {filters.condominiumLink === 'true' && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="sm:col-span-2 lg:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Qual condomínio</label>
                    <Select
                      name="condominiumId"
                      value={
                        filters.condominiumId
                          ? condominiumSelectOptions.find((opt) => opt.value === filters.condominiumId)
                          : null
                      }
                      onChange={(option) => handleFilterChange('condominiumId', option?.value || '')}
                      options={condominiumSelectOptions}
                      styles={customStyles}
                      placeholder={
                        condominiumsLoading
                          ? 'Carregando condomínios...'
                          : `Todos com condomínio vinculado (${totalLinkedToCondominium})`
                      }
                      isClearable={Boolean(filters.condominiumId)}
                      isLoading={condominiumsLoading}
                      isDisabled={condominiumsLoading}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Controles de visualização e contagem */}
          <div className="flex flex-row gap-2 sm:gap-4 items-center justify-between">
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <span className="text-gray-600 text-sm sm:text-base">
                {viewType === 'map' ? visibleProperties.length : totalProperties} imóveis encontrados
              </span>
              <div className="hidden sm:flex items-center gap-2 sm:ml-4">
                <button
                  onClick={() => setViewType('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewType === 'grid' 
                      ? '' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  style={viewType === 'grid' ? {
                    color: 'var(--secondary-color, #ffffff)',
                    backgroundColor: 'var(--primary-color, #3B82F6)'
                  } : {}}
                  title="Visualização em grid"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewType('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewType === 'list' 
                      ? '' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  style={viewType === 'list' ? {
                    color: 'var(--secondary-color, #ffffff)',
                    backgroundColor: 'var(--primary-color, #3B82F6)'
                  } : {}}
                  title="Visualização em lista"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </button>
                {mapSettings.enabled && (
                  <button
                    onClick={() => setViewType('map')}
                    className={`p-2 rounded-md transition-colors ${
                      viewType === 'map' 
                        ? '' 
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewType === 'map' ? {
                      color: 'var(--secondary-color, #ffffff)',
                      backgroundColor: 'var(--primary-color, #3B82F6)'
                    } : {}}
                    title="Visualização em mapa"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <div className="min-w-[120px] sm:min-w-[140px]">
                <Select
                  value={filters.orderBy ? orderByOptions.find(option => option.value === filters.orderBy) : null}
                  onChange={(option) => handleFilterChange('orderBy', option?.value || '')}
                  options={orderByOptions}
                  styles={{
                    ...customStyles,
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? 'var(--primary-color, #000000)' : '#cfd4dd',
                      borderRadius: '0.5rem',
                      minHeight: '40px',
                      height: '40px',
                      boxShadow: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: 'var(--primary-color, #000000)'
                      },
                      '&:focus, &:focus-within': {
                        border: '2px solid var(--primary-color, #000000)',
                        boxShadow: 'none'
                      }
                    }),
                    singleValue: (base, { selectProps }) => ({
                      ...base,
                      color: selectProps.hasValue ? '#111827' : '#6B7280',
                      fontSize: '14px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#6b7280',
                      fontSize: '14px'
                    })
                  }}
                  menuPortalTarget={document.body}
                  placeholder="Ordenar por"
                  isSearchable={false}
                />
              </div>
            </div>
          </div>
        </form>
        </Suspense>
          </div>

       {/* Conteúdo principal - condicional para largura */}
       {viewType === 'map' ? (
         <div className="flex-1 w-full relative flex flex-col md:flex-row lg:flex-row">
           {/* Mapa */}
           <div 
             ref={propertiesRef}
             data-animate="properties"
             className="h-[60vh] md:h-[calc(100dvh-300px)] lg:h-[calc(100dvh-300px)] relative z-0 flex-1 md:flex-1 lg:flex-1 min-h-[400px]"
             style={{ zIndex: 1 }}
           >
            {mapSettings.provider === 'mapbox' ? (
              <MapErrorBoundary>
                <Suspense fallback={<MapViewFallback />}>
                  <MapboxPropertyMap 
                    key={mapKey}
                    properties={properties}
                    onPropertySelect={setSelectedPropertyId}
                    selectedPropertyId={selectedPropertyId}
                    hoveredPropertyId={hoveredPropertyId}
                    onMapViewChange={setVisibleProperties}
                    mapboxToken={mapSettings.mapboxToken}
                    mapboxStyle={mapSettings.mapboxStyle}
                    toggleFavorite={toggleFavorite}
                    isFavorite={isFavorite}
                    addToComparison={addToComparison}
                    removeFromComparison={removeFromComparison}
                    isInComparison={isInComparison}
                    showNotification={showNotification}
                    setShareModal={setShareModal}
                  />
                </Suspense>
              </MapErrorBoundary>
            ) : (
              <MapErrorBoundary>
                <Suspense fallback={<MapViewFallback />}>
                  <PropertyMap 
                    key={mapKey}
                    properties={properties}
                    onPropertySelect={setSelectedPropertyId}
                    selectedPropertyId={selectedPropertyId}
                    hoveredPropertyId={hoveredPropertyId}
                    onMapViewChange={setVisibleProperties}
                    toggleFavorite={toggleFavorite}
                    isFavorite={isFavorite}
                    addToComparison={addToComparison}
                    removeFromComparison={removeFromComparison}
                    isInComparison={isInComparison}
                    showNotification={showNotification}
                    setShareModal={setShareModal}
                  />
                </Suspense>
              </MapErrorBoundary>
            )}
           </div>
           
            {/* Lista */}
            <div 
              className="h-[40vh] md:h-[calc(100dvh-300px)] lg:h-[calc(100dvh-300px)] bg-white shadow-2xl border-t md:border-t-0 lg:border-t-0 md:border-l lg:border-l border-gray-200 w-full md:w-1/2 lg:w-[650px] min-h-[300px]"
              style={{ zIndex: 1000 }}
            >
             {/* Lista de propriedades */}
             <div className="overflow-y-auto h-full p-2">
               <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-2 relative z-10">
                 {visibleProperties.map((property) => (
                   <div 
                     key={property.id}
                     className={`border rounded-lg overflow-hidden shadow-sm bg-white cursor-pointer transition-all duration-200 hover:shadow-md relative z-10 ${
                       selectedPropertyId === property.id ? 'ring-2 shadow-md' : ''
                     }`}
                     style={{
                       zIndex: 10,
                       ...(selectedPropertyId === property.id ? {
                         '--tw-ring-color': 'var(--primary-color, #3B82F6)',
                         ringColor: 'var(--primary-color, #3B82F6)'
                       } : {})
                     }}
                     onClick={() => {
                       // Se já está selecionado, deseleciona
                       if (selectedPropertyId === property.id) {
                         setSelectedPropertyId(null);
                       } else {
                         setSelectedPropertyId(property.id);
                       }
                     }}
                     onMouseEnter={() => setHoveredPropertyId(property.id)}
                     onMouseLeave={() => setHoveredPropertyId(null)}
                   >
                     {/* Container da imagem */}
                     <div className="relative h-[220px] overflow-hidden">
                       <div className="w-full h-full transform transition-transform duration-700 hover:scale-110">
                         <img
                           src={property.coverImage || property.images[0]}
                           alt={property.title}
                           className="w-full h-full object-cover object-center"
                           style={{ 
                             width: '100%', 
                             height: '100%', 
                             objectFit: 'cover',
                             objectPosition: 'center',
                             display: 'block'
                           }}
                         />
                       </div>
                       
                       {/* Preço sobreposto */}
                       <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
                         <p className="text-white text-base font-lato font-medium bg-gray-900/50 px-2 py-0.5 rounded backdrop-blur-sm">
                           {property.purpose === 'sale' 
                             ? `R$ ${Number(property.price).toLocaleString()}`
                             : `R$ ${Number(property.price).toLocaleString()}/mês`
                           }
                         </p>
                       </div>

                       {/* Badges */}
                       <div className="absolute top-2 left-2 flex gap-2">
                         <span className="bg-gray-900/50 text-white px-2 py-1 rounded text-xs font-medium backdrop-blur-sm">
                           {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
                         </span>
                         {(property.showInHome || property.isFeatured || property.displayTag === 'featured') && (
                           <span className="bg-yellow-500/90 text-white px-2 py-1 rounded text-xs font-medium backdrop-blur-sm flex items-center gap-1">
                             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                               <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
                             </svg>
                           </span>
                         )}
                       </div>

                       {/* Ícones de recursos */}
                       <div className="absolute top-2 right-2 flex gap-2">
                         {property.tour360?.type && (
                           <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm">
                             <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white">
                               <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                               <path d="M12 3C14.5 3 17 7 17 12C17 17 14.5 21 12 21C9.5 21 7 17 7 12C7 7 9.5 3 12 3Z" stroke="currentColor" strokeWidth="1.5" />
                               <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5" />
                             </svg>
                           </div>
                         )}
                         {property.videoUrl && (
                           <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm">
                             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-white">
                               <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                             </svg>
                           </div>
                         )}
                       </div>
                     </div>

                    {/* Conteúdo do card */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[0.70rem] font-semibold text-gray-600">
                            {property.type === 'house' ? 'CASA' : 
                             property.type === 'apartment' ? 'APARTAMENTO' : 
                             property.type === 'loft' ? 'LOFT' :
                             property.type === 'warehouse' ? 'GALPÃO' : 
                             property.type === 'store' ? 'LOJA' : 
                             property.type === 'building' ? 'PRÉDIO' : 
                             property.type === 'office' ? 'SALA' : 
                             property.type === 'land' ? 'TERRENO' : 
                             property.type === 'farm' ? 'SÍTIO' : 
                             property.type === 'countryHouse' ? 'CHÁCARA' : 'IMÓVEL'} - {property.code}
                          </span>
                          {property.isReserved && (
                            <span className="bg-red-100 text-red-800 text-[0.62rem] px-2 py-1 rounded-full font-semibold uppercase">
                              Reservado
                            </span>
                          )}
                        </div>
                        
                        {/* Botões de ação */}
                        <div className="flex items-center gap-1">
                          {/* Favoritos */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(property);
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                            title={isFavorite(property.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              fill={isFavorite(property.id) ? "currentColor" : "none"} 
                              viewBox="0 0 24 24" 
                              strokeWidth={1.5} 
                              stroke="currentColor" 
                              className={`w-3.5 h-3.5 transition-colors ${
                                isFavorite(property.id) 
                                  ? "text-red-500" 
                                  : "text-gray-400 group-hover:text-red-500"
                              }`}
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" 
                              />
                            </svg>
                          </button>

                          {/* Comparação */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              if (isInComparison(property.id)) {
                                // Remove da comparação
                                await removeFromComparison(property.id);
                                showNotification('Imóvel removido da comparação', 'warning', 3000);
                              } else {
                                // Adiciona à comparação
                                const result = await addToComparison(property);
                                if (result.success) {
                                  // Mostra notificação de sucesso
                                  showNotification(result.message, 'success', 3000);
                                } else {
                                  // Mostra notificação de erro
                                  showNotification(result.message, 'error', 5000);
                                }
                              }
                            }}
                            className={`p-1 rounded-full hover:bg-gray-100 transition-colors group relative ${
                              isInComparison(property.id) ? 'bg-white' : ''
                            }`}
                            title={isInComparison(property.id) ? "Remover da comparação" : "Adicionar à comparação"}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              fill={isInComparison(property.id) ? "#3B82F6" : "none"}
                              viewBox="0 0 24 24" 
                              strokeWidth={1.5} 
                              stroke="currentColor" 
                              className={`w-3.5 h-3.5 transition-colors ${
                                isInComparison(property.id) 
                                  ? "text-blue-500" 
                                  : "text-gray-400 group-hover:text-blue-500"
                              }`}
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                              />
                            </svg>
                          </button>

                          {/* Compartilhar */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareModal({ isOpen: true, property });
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                            title="Compartilhar"
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              strokeWidth={1.5} 
                              stroke="currentColor" 
                              className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-500 transition-colors"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.935-2.186 2.25 2.25 0 00-3.935 2.186z" 
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                       
                       <h4 className="font-semibold text-sm text-gray-900 line-clamp-2 mb-2">
                         {property.title}
                       </h4>
                       
                       <p className="text-xs text-gray-600 mb-3 flex items-center gap-2">
                         <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                           <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                           <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                         </svg>
                         <span className="truncate">{getPropertyDisplayAddress(property)}</span>
                       </p>
                       
                       {/* Grid de informações */}
                       <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                         <div className="flex items-center gap-2">
                           <i className="flaticon-bed text-base"></i>
                           <span className="font-medium">{property.bedrooms} quartos</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <i className="flaticon-bathtub text-base"></i>
                           <span className="font-medium">{property.bathrooms} banheiros</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <i className="flaticon-square text-base"></i>
                           <span className="font-medium">{property.area}m²</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <i className="flaticon-car text-base"></i>
                           <span className="font-medium">{property.parkingSpaces} vagas</span>
                         </div>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
               
               {visibleProperties.length === 0 && (
                 <div className="p-8 text-center text-gray-500">
                   <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                   </svg>
                   <p>Nenhuma propriedade visível no mapa</p>
                 </div>
               )}
             </div>
           </div>
        </div>
      ) : (
        <div className="max-w-[85rem] mx-auto p-6 pt-0 w-full">
          {loading && properties.length === 0 ? (
            <PropertyGridSkeleton count={8} />
          ) : (
            <div>
              <div 
                ref={propertiesRef}
                data-animate="properties"
                className={`
                  ${viewType === 'grid' 
                    ? 'grid grid-cols-1 min-[560px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'
                    : 'flex flex-col gap-4'
                  } 
                `}
              >
                {properties.map((property, index) => (
              <div 
                key={property.id}
                ref={(el) => {
                  if (el) {
                    propertyRefs.current[property.id] = el;
                  }
                }}
                data-animate="property-card"
                data-card-id={property.id}
                className={`
                  border rounded-lg overflow-hidden shadow-md bg-white transition-all duration-500 transform hover:shadow-[0_0_30px_-5px_rgba(0,0,0,0.2)]
                  ${viewType === 'grid' ? 'min-h-[450px] flex flex-col' : 'flex flex-row'}
                  ${visibleCards[property.id] 
                    ? 'opacity-100 translate-y-0 scale-100' 
                    : 'opacity-0 translate-y-8 scale-95'
                  }
                `}
              >
                {/* Container da imagem */}
                <div 
                  className={`
                    relative overflow-hidden
                    ${viewType === 'grid' ? 'h-[220px]' : 'w-[400px]'}
                  `}
                >
                  <Link
                    to={buildPropertyPath(property)}
                    className="block w-full h-full"
                    aria-label={`Ver detalhes: ${property.title}`}
                  >
                  <div className={`
                    w-full h-full transform transition-transform duration-700 hover:scale-110 relative
                    ${viewType === 'list' ? 'aspect-[4/3]' : ''}
                  `}>
                    <LazyLoadImage
                      src={property.coverImage || property.images[0]}
                      alt={generatePropertyImageAlt(property)}
                      className={`
                        w-full h-full object-cover absolute inset-0
                        ${viewType === 'list' ? 'aspect-[4/3]' : ''}
                      `}
                      wrapperClassName="w-full h-full block bg-gray-100"
                      effect="blur"
                      placeholder={
                        <div className="w-full h-full bg-gray-200 animate-pulse"></div>
                      }
                    />
                  </div>
                  </Link>

                  {/* Preço e Status */}
                  <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center pointer-events-none">
                    <p className="text-white text-base font-lato font-medium bg-gray-900/50 px-2 py-0.5 rounded backdrop-blur-sm">
                      {property.purpose === 'sale' 
                        ? `R$ ${Number(property.price).toLocaleString()}`
                        : `R$ ${Number(property.price).toLocaleString()}/mês`
                      }
                    </p>
                  </div>

                  {/* Badges e informações sobrepostas na imagem */}
                  <div className="absolute top-2 left-2 flex gap-2 pointer-events-none">
                    <span className="bg-gray-900/50 text-white px-3 py-1 rounded text-sm backdrop-blur-sm">
                      {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
                    </span>
                    {(property.showInHome || property.isFeatured || property.displayTag === 'featured') && (
                      <span className="bg-yellow-500/90 text-white px-2 py-1 rounded text-sm backdrop-blur-sm flex items-center gap-1 group relative">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                          <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
                        </svg>
                        <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-8 left-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                          Imóvel em destaque
                        </div>
                      </span>
                    )}
                  </div>

                  {/* Ícones no topo direito */}
                  <div className="absolute top-2 right-2 flex gap-2">
                    {property.tour360?.type && (
                      <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
                        <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                          {property.tour360.type === 'matterport' ? 'Contém tour 3D/360°' : property.tour360.type === 'edm360' ? 'Contém tour 360°' : 'Contém tour 360° Marzipano'}
                        </div>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white">
                          {/* Globo/Mundo 360° */}
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                          {/* Linhas horizontais do globo */}
                          <path d="M12 3C14.5 3 17 7 17 12C17 17 14.5 21 12 21C9.5 21 7 17 7 12C7 7 9.5 3 12 3Z" stroke="currentColor" strokeWidth="1.5" />
                          {/* Linhas verticais do globo */}
                          <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M4.5 7.5H19.5" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M4.5 16.5H19.5" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                      </div>
                    )}
                    {property.videoUrl && (
                      <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
                        <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                          Contém vídeo
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-white">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                        </svg>
                      </div>
                    )}
                    {property.floorPlans && property.floorPlans.length > 0 && (
                      <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
                        <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                          Contém planta baixa
                        </div>
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 24 24" 
                          className="w-5 h-5 text-white" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="1.5"
                        >
                          <rect x="2" y="2" width="20" height="20" rx="2" />
                          <path d="M2 7h20" />
                          <path d="M12 7v15" />
                          <path d="M7 7v8" />
                          <path d="M17 7v8" />
                          <path d="M7 11h5" />
                          <path d="M12 15h5" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conteúdo do card */}
                <div className={`
                  ${viewType === 'grid' 
                    ? 'p-4 flex flex-col flex-1 justify-between'
                    : 'flex-1 p-4 flex flex-col justify-between'
                  }
                `}>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-medium text-gray-500 flex items-center gap-2">
                        {property.type === 'house' ? 'CASA' : 
                         property.type === 'apartment' ? 'APARTAMENTO' : 
                         property.type === 'loft' ? 'LOFT' :
                         property.type === 'warehouse' ? 'GALPÃO' : 
                         property.type === 'store' ? 'LOJA' : 
                         property.type === 'building' ? 'PRÉDIO' : 
                         property.type === 'office' ? 'SALA' : 
                         property.type === 'land' ? 'TERRENO' : 
                         property.type === 'farm' ? 'SÍTIO' : 
                         property.type === 'countryHouse' ? 'CHÁCARA' : 'IMÓVEL'} - {property.code}
                        {property.displayTag && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase whitespace-nowrap ${
                            property.displayTag === 'reserved' ? 'bg-red-100 text-red-800' :
                            property.displayTag === 'preLaunch' ? 'bg-blue-100 text-blue-800' :
                            property.displayTag === 'launch' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {property.displayTag === 'reserved' ? 'Reservado' :
                             property.displayTag === 'preLaunch' ? 'Pré-lançamento' :
                             property.displayTag === 'launch' ? 'Lançamento' :
                             property.displayTag}
                          </span>
                        )}
                      </span>
                      
                      {/* Botões de ação */}
                      <div className="flex items-center gap-1">
                        {/* Favoritos */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(property);
                          }}
                          className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                          title={isFavorite(property.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill={isFavorite(property.id) ? "currentColor" : "none"} 
                            viewBox="0 0 24 24" 
                            strokeWidth={1.5} 
                            stroke="currentColor" 
                            className={`w-4 h-4 transition-colors ${
                              isFavorite(property.id) 
                                ? "text-red-500" 
                                : "text-gray-400 group-hover:text-red-500"
                            }`}
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" 
                            />
                          </svg>
                        </button>

                        {/* Comparação */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            
                            if (isInComparison(property.id)) {
                              // Remove da comparação
                              await removeFromComparison(property.id);
                              showNotification('Imóvel removido da comparação', 'warning', 3000);
                            } else {
                              // Adiciona à comparação
                              const result = await addToComparison(property);
                              if (result.success) {
                                // Mostra notificação de sucesso
                                showNotification(result.message, 'success', 3000);
                              } else {
                                // Mostra notificação de erro
                                showNotification(result.message, 'error', 5000);
                              }
                            }
                          }}
                          className={`p-1 rounded-full hover:bg-gray-100 transition-colors group relative ${
                            isInComparison(property.id) ? 'bg-white' : ''
                          }`}
                          title={isInComparison(property.id) ? "Remover da comparação" : "Adicionar à comparação"}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill={isInComparison(property.id) ? "#3B82F6" : "none"}
                            viewBox="0 0 24 24" 
                            strokeWidth={1.5} 
                            stroke="currentColor" 
                            className={`w-4 h-4 transition-colors ${
                              isInComparison(property.id) 
                                ? "text-blue-500" 
                                : "text-gray-400 group-hover:text-blue-500"
                            }`}
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                            />
                          </svg>
                        </button>

                        {/* Compartilhar */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareModal({ isOpen: true, property });
                          }}
                          className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                          title="Compartilhar"
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            strokeWidth={1.5} 
                            stroke="currentColor" 
                            className="w-4 h-4 text-gray-400 group-hover:text-green-500 transition-colors"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.935-2.186 2.25 2.25 0 00-3.935 2.186z" 
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h3 
                      className={`
                        font-montserrat font-semibold text-[#444444] line-clamp-2 transition-colors duration-200 hover:text-[var(--primary-color,#3B82F6)]
                        ${viewType === 'grid' ? 'text-[1.1rem] mb-2' : 'text-xl mb-3'}
                      `}
                    >
                      <Link to={buildPropertyPath(property)}>
                        {property.title}
                      </Link>
                    </h3>
                  </div>

                  <div>
                    <p className="font-lato text-gray-600 mb-4 flex items-center gap-2 text-[13px]">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                      </svg>
                      {getPropertyDisplayAddress(property)}
                    </p>

                    {/* Grid de informações */}
                    <div className={`
                      ${viewType === 'grid'
                        ? 'grid grid-cols-2 gap-4'
                        : 'flex flex-wrap gap-6'
                      } text-sm font-lato text-gray-500
                    `}>
                      <div className="flex items-center gap-3">
                        <i className="flaticon-bed text-lg"></i>
                        <span>{property.bedrooms} Quartos</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="flaticon-bathtub text-lg"></i>
                        <span>{property.bathrooms} Banheiros</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="flaticon-square text-lg"></i>
                        <span>{property.area}m² priv.</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="flaticon-car text-lg"></i>
                        <span>{property.parkingSpaces} Vagas</span>
                      </div>
                    </div>

                    {/* Linha divisória */}
                    <div className="border-t border-gray-200 my-4"></div>

                    {/* Botões */}
                    <div className="flex gap-2">
                      <Link 
                        to={buildPropertyPath(property)}
                        className="flex-1 rounded-lg py-2 transition-all duration-300 text-center no-underline"
                        style={{
                          backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                          color: 'var(--btn-outline-text, #3B82F6)',
                          border: '1px solid var(--btn-outline-border, #3B82F6)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                          e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                          e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                        }}
                      >
                        Ver detalhes
                      </Link>
                    </div>
                  </div>

                  {/* Botões de ação - Admin ou Corretor com permissões */}
                  {(() => {
                    const actions = canViewPropertyActions(property);
                    if (!actions.canEdit && !actions.canDelete && !actions.canDuplicate) {
                      return null;
                    }
                    return (
                    <div className="flex justify-between mt-2 border-t border-gray-100 pt-2">
                        {actions.canEdit && (
                          <>
                      <button
                        onClick={() => setQuickEditModal({ isOpen: true, property })}
                        className="p-2 text-blue-500 hover:text-blue-600 flex-1 flex justify-center"
                        title="Editar"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                            {(actions.canDuplicate || actions.canDelete) && <div className="w-px bg-gray-100"></div>}
                          </>
                        )}
                        {actions.canDuplicate && (
                          <>
                      <button
                        onClick={() => handleDuplicate(property)}
                        className="p-2 text-green-500 hover:text-green-600 flex-1 flex justify-center"
                        title="Duplicar"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                        </svg>
                      </button>
                            {actions.canDelete && <div className="w-px bg-gray-100"></div>}
                          </>
                        )}
                        {actions.canDelete && (
                      <button
                        onClick={() => openDeleteModal(property)}
                        className="p-2 text-red-500 hover:text-red-600 flex-1 flex justify-center"
                        title="Remover"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                  )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
              </div>

              {/* Elemento sentinela para scroll infinito */}
              {hasMoreProperties && !loading && viewType !== 'map' && (
                <div ref={loadMoreRef} className="flex justify-center mt-8 mb-4">
                  <div className="animate-pulse text-gray-400 text-sm">
                    Carregando mais imóveis...
                  </div>
                </div>
              )}

              {!hasMoreProperties && properties.length > 0 && viewType !== 'map' && (
                <div className="flex justify-center mt-8 mb-4">
                  <p className="text-gray-500 text-sm">Você chegou ao final da lista</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

        {/* Modal de Confirmação */}
        {deleteModal.isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setDeleteModal({ isOpen: false, property: null })}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-montserrat font-semibold mb-4">Confirmar Exclusão</h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja excluir a propriedade "<span className="text-black">{deleteModal.property?.title}</span>"?
              </p>
              <div className="flex justify-between gap-3">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, property: null })}
                  className="px-4 py-2 rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  Não
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Sim
                </button>
              </div>
            </div>
          </div>
        )}

        {quickEditModal.isOpen && (
          <Suspense fallback={null}>
            <QuickEditModal
              isOpen={quickEditModal.isOpen}
              onClose={() => setQuickEditModal({ isOpen: false, property: null })}
              property={quickEditModal.property}
              onSave={handleQuickEdit}
              onAdvancedEdit={() => {
                setQuickEditModal({ isOpen: false, property: null });
                navigate(`/editar/${quickEditModal.property.id}`);
              }}
            />
          </Suspense>
        )}

        {/* Modal de Compartilhamento */}
        {shareModal.isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShareModal({ isOpen: false, property: null })}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Compartilhar Imóvel</h3>
                <button
                  onClick={() => setShareModal({ isOpen: false, property: null })}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>{shareModal.property?.title}</strong>
                </p>
                <p className="text-xs text-gray-500">
                  {getPropertyDisplayAddress(shareModal.property)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* WhatsApp */}
                <button
                  onClick={() => {
                    shareToWhatsApp(shareModal.property);
                    setShareModal({ isOpen: false, property: null });
                  }}
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors"
                >
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  <span className="text-sm font-medium">WhatsApp</span>
                </button>

                {/* Facebook */}
                <button
                  onClick={() => {
                    shareToFacebook(shareModal.property);
                    setShareModal({ isOpen: false, property: null });
                  }}
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#1877F2]/10 hover:border-[#1877F2] transition-colors"
                >
                  <svg className="w-6 h-6" fill="#1877F2" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-sm font-medium">Facebook</span>
                </button>

                {/* LinkedIn */}
                <button
                  onClick={() => {
                    shareToLinkedIn(shareModal.property);
                    setShareModal({ isOpen: false, property: null });
                  }}
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#0A66C2]/10 hover:border-[#0A66C2] transition-colors"
                >
                  <svg className="w-6 h-6" fill="#0A66C2" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="text-sm font-medium">LinkedIn</span>
                </button>

                {/* Email */}
                <button
                  onClick={() => {
                    shareToEmail(shareModal.property);
                    setShareModal({ isOpen: false, property: null });
                  }}
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium">Email</span>
                </button>
              </div>

              {/* Copiar Link */}
              <button
                onClick={() => {
                  copyLink(shareModal.property);
                  setShareModal({ isOpen: false, property: null });
                }}
                className="w-full mt-4 flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Copiar Link</span>
              </button>
            </div>
          </div>
        )}
        
        <ScrollToTop />
    </div>
  );
}

export default PropertyList; 